import React, { useState, useEffect, useMemo } from "react"
import {
  Modal,
  Button,
  Input,
  Select,
  Switch,
  Checkbox,
  DatePicker,
  TimePicker,
} from "antd"
import {
  InfoCircleOutlined,
  UsergroupAddOutlined,
  CloseOutlined,
} from "@ant-design/icons"
import dayjs from "dayjs"
import { translations } from "../../utils/Translation/staticTranslation"
import DiscountAudienceStep from "./DiscountAudienceStep"
import { Discounts as DiscountsApi } from "../../Apis/discountsApi"
import { message } from "antd"
import "./Discounts.css"

export function normalizePlanNameKey(name) {
  if (typeof name !== "string") return ""
  return name.trim().replace(/\s+/g, " ").toLowerCase()
}

export function coercePlanNamesList(raw) {
  if (raw == null) return []
  if (Array.isArray(raw)) {
    return raw
      .map((entry) => {
        if (typeof entry === "string") return entry
        if (entry && typeof entry === "object" && typeof entry.name === "string")
          return entry.name
        if (entry != null && typeof entry !== "object") return String(entry)
        return ""
      })
      .filter((s) => s && String(s).trim() !== "")
  }
  if (typeof raw === "string") {
    return raw
      .split(/[,;|\n]+/g)
      .map((s) => s.trim())
      .filter(Boolean)
  }
  return []
}

export function extractPlansArrayFromGetPlansResponse(data) {
  if (!data) return []
  if (Array.isArray(data)) return data
  if (typeof data === "object" && Array.isArray(data.result)) return data.result
  return []
}

export function dedupeTargetPlanLabels(englishPlans, arabicPlans) {
  const english = coercePlanNamesList(englishPlans)
  const arabic = coercePlanNamesList(arabicPlans)
  const seen = new Set()
  const outEn = []
  const outAr = []
  english.forEach((plan, i) => {
    if (!plan) return
    if (plan === "All Plans") {
      if (!seen.has("__all_plans__")) {
        seen.add("__all_plans__")
        outEn.push(plan)
        outAr.push(arabic[i] ?? "")
      }
      return
    }
    const key = normalizePlanNameKey(plan)
    if (!key || seen.has(key)) return
    seen.add(key)
    outEn.push(plan)
    outAr.push(arabic[i] ?? "")
  })
  return { plans: outEn, plansArabic: outAr }
}

const { Option } = Select

const DISCOUNT_FIELD_LABEL_STYLE = {
  display: "block",
  fontSize: "14px",
  fontWeight: 400,
  color: "#141414",
}

function DiscountFieldLabel({ required = false, children }) {
  return (
    <label style={DISCOUNT_FIELD_LABEL_STYLE}>
      {children}
      {required ? <span className="discount-field-required">*</span> : null}
    </label>
  )
}

const CreateDiscountModal = ({ visible, onClose, onSubmit, editData }) => {
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.discountsPage

  const [currentStep, setCurrentStep] = useState(0)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showDiscardModal, setShowDiscardModal] = useState(false)
  const [plans, setPlans] = useState([])
  const [plansLoading, setPlansLoading] = useState(false)

  const applicablePlanSelectOptions = useMemo(() => {
    const allEntry = plans.find((p) => p.id === "all")
    const seen = new Set()
    const deduped = []
    for (const p of plans) {
      if (p.id === "all") continue
      const key = normalizePlanNameKey(p.name)
      if (!key || seen.has(key)) continue
      seen.add(key)
      deduped.push(p)
    }
    return allEntry ? [allEntry, ...deduped] : deduped
  }, [plans])

  const getMinPriceForPlanName = (planName) => {
    const key = normalizePlanNameKey(planName)
    let min = Infinity
    for (const p of plans) {
      if (p.id === "all") continue
      if (normalizePlanNameKey(p.name) !== key) continue
      const n = Number(p.price)
      if (!Number.isNaN(n)) min = Math.min(min, n)
    }
    return min === Infinity ? null : min
  }
  const [countries, setCountries] = useState([])
  const [countriesLoading, setCountriesLoading] = useState(false)
  const [discountCodeError, setDiscountCodeError] = useState("")
  const [fixedDiscountError, setFixedDiscountError] = useState("")
  const [planExtensionInput, setPlanExtensionInput] = useState("1")
  const [formData, setFormData] = useState({
    discountNameEnglish: "",
    discountNameArabic: "",
    discountCode: "",
    discountType: "percentage",
    discountPercentage: 0,
    discountFixed: 0,
    applicablePlans: [],
    targetCountries: [],
    startDate: null,
    startTime: null,
    endDate: null,
    endTime: null,
    maxUses: "",
    notifyWhenMaxReached: false,
    firstPurchaseOnly: false,
    redeemMoreThanOnce: false,
    showBanner: false,
    bannerTextEnglish: "",
    bannerTextArabic: "",
    audience: "",
    selectedUsers: [],
    planExtensionMonths: 1,
  })

  // Populate form when editData changes
  useEffect(() => {
    if (editData && visible) {
      const discountCode = editData.discountCode || ""
      const showBannerValue =
        editData.showBanner ?? editData.show_banner ?? false
      const bannerEnglishValue =
        editData.bannerTextEnglish ??
        editData.english_text ??
        editData.banner_text_english ??
        ""
      const bannerArabicValue =
        editData.bannerTextArabic ??
        editData.arabic_text ??
        editData.banner_text_arabic ??
        ""
      setFormData({
        discountNameEnglish: editData.discountNameEnglish || "",
        discountNameArabic: editData.discountNameArabic || "",
        discountCode: discountCode,
        discountType: editData.discountType || "percentage",
        discountPercentage: editData.discountPercentage || 0,
        discountFixed: editData.discountFixed || 0,
        applicablePlans: (() => {
          const raw = Array.isArray(editData.applicablePlans)
            ? editData.applicablePlans
            : editData.applicablePlans === "All Plans"
            ? []
            : editData.applicablePlans
            ? [editData.applicablePlans]
            : []
          return dedupeTargetPlanLabels(raw, []).plans
        })(),
        targetCountries: Array.isArray(editData.targetCountries)
          ? editData.targetCountries
          : editData.targetCountries === "All Countries"
          ? []
          : editData.targetCountries
          ? [editData.targetCountries]
          : [],
        startDate: editData.startDate || null,
        startTime: editData.startTime || null,
        endDate: editData.endDate || null,
        endTime: editData.endTime || null,
        maxUses: editData.maxUses || "",
        notifyWhenMaxReached: editData.notifyWhenMaxReached || false,
        firstPurchaseOnly: editData.firstPurchaseOnly || false,
        redeemMoreThanOnce: editData.redeemMoreThanOnce || false,
        showBanner: showBannerValue,
        bannerTextEnglish: bannerEnglishValue,
        bannerTextArabic: bannerArabicValue,
        audience: editData.audience || "",
        selectedUsers: editData.selectedUsers || [],
        planExtensionMonths: editData.planExtensionMonths || 1,
      })
      setPlanExtensionInput(String(editData.planExtensionMonths || 1))
      // Validate discount code when editing
      setDiscountCodeError(validateDiscountCode(discountCode))
      setFixedDiscountError("") // Clear fixed discount error
      setCurrentStep(0) // Reset to first step when editing
    } else if (!editData && visible) {
      // Reset form when creating new discount
      setFormData({
        discountNameEnglish: "",
        discountNameArabic: "",
        discountCode: "",
        discountType: "percentage",
        discountPercentage: 0,
        discountFixed: 0,
        applicablePlans: [],
        targetCountries: [],
        startDate: null,
        startTime: null,
        endDate: null,
        endTime: null,
        maxUses: "",
        notifyWhenMaxReached: false,
        firstPurchaseOnly: false,
        redeemMoreThanOnce: false,
        showBanner: false,
        bannerTextEnglish: "",
        bannerTextArabic: "",
        audience: "",
        selectedUsers: [],
        planExtensionMonths: 1,
      })
      setPlanExtensionInput("1")
      setDiscountCodeError("")
      setFixedDiscountError("")
      setCurrentStep(0)
    }
  }, [editData, visible])

  // Validate discount code
  const validateDiscountCode = (code) => {
    if (!code || code.trim() === "") {
      return ""
    }

    // Check for invalid characters (only allow A-Z, 0-9, underscore, and hyphen)
    const validPattern = /^[A-Za-z0-9_-]+$/
    if (!validPattern.test(code)) {
      return (
        t?.modal?.error_discount_code_invalid_chars ||
        "Only letters (A-Z), numbers (0-9), underscore (_), and hyphen (-) are allowed"
      )
    }

    // Check length
    if (code.length < 3) {
      return (
        t?.modal?.error_discount_code_min_length ||
        "Discount code must be at least 3 characters"
      )
    }

    if (code.length > 20) {
      return (
        t?.modal?.error_discount_code_max_length ||
        "Discount code must not exceed 20 characters"
      )
    }

    return ""
  }

  const MAX_BANNER_CHARS = 70
  const countChars = (text) =>
    !text || typeof text !== "string" ? 0 : text.trim().length

  // Keep input as typed; validation uses trimmed length (matches Announcements)
  const limitBannerChars = (text) => {
    if (!text || typeof text !== "string") return ""
    return text
  }

  const handleInputChange = (field, value) => {
    // Special handling for discountCode validation
    if (field === "discountCode") {
      // Convert to uppercase and remove spaces
      const sanitizedValue = value.toUpperCase().replace(/\s/g, "")

      // Validate the sanitized value
      const error = validateDiscountCode(sanitizedValue)
      setDiscountCodeError(error)

      setFormData((prev) => {
        return { ...prev, [field]: sanitizedValue }
      })
      return
    }

    if (field === "bannerTextEnglish" || field === "bannerTextArabic") {
      const sanitizedValue = limitBannerChars(value)
      setFormData((prev) => ({ ...prev, [field]: sanitizedValue }))
      return
    }

    setFormData((prev) => {
      // Special handling for applicablePlans
      if (field === "applicablePlans") {
        // Empty array means "All Plans" - no special handling needed
        // Just set the value directly
        return { ...prev, [field]: value || [] }
      }
      return { ...prev, [field]: value }
    })
  }

  const handleFormDataChange = (updater) => {
    if (typeof updater === "function") {
      setFormData(updater)
    } else {
      setFormData((prev) => ({
        ...prev,
        ...updater,
      }))
    }
  }

  // Validate all required fields in step 0 (Basic Info)
  const isStep0Valid = () => {
    // Check discount name English
    if (!formData.discountNameEnglish?.trim()) {
      return false
    }

    // Check discount code - must be valid and at least 3 characters
    if (
      !formData.discountCode?.trim() ||
      discountCodeError !== "" ||
      formData.discountCode.length < 3
    ) {
      return false
    }

    // Check discount value based on type (must be between 0 and 99)
    if (formData.discountType === "percentage") {
      if (
        !formData.discountPercentage ||
        formData.discountPercentage <= 0 ||
        formData.discountPercentage > 99
      ) {
        return false
      }
    } else if (formData.discountType === "fixed") {
      if (
        !formData.discountFixed ||
        formData.discountFixed <= 0 ||
        formData.discountFixed > 99999 ||
        fixedDiscountError !== ""
      ) {
        return false
      }

      // Check if discount value is less than selected plan price
      if (formData.applicablePlans && formData.applicablePlans.length > 0) {
        const selectedPlanName = formData.applicablePlans[0]
        const minPrice = getMinPriceForPlanName(selectedPlanName)
        if (minPrice !== null && minPrice > 0) {
          if (formData.discountFixed >= minPrice) {
            return false
          }
        }
      }
    } else if (formData.discountType === "plan_extension") {
      if (
        !formData.planExtensionMonths ||
        formData.planExtensionMonths < 1 ||
        formData.planExtensionMonths > 10
      ) {
        return false
      }
    }

    // Check start date and time
    if (!formData.startDate || !formData.startTime) {
      return false
    }

    // Check end date and time
    if (!formData.endDate || !formData.endTime) {
      return false
    }

    // Applicable plans are required for every discount type
    const selectedPlans = formData.applicablePlans || []
    if (!Array.isArray(selectedPlans) || selectedPlans.length === 0) {
      return false
    }

    if (formData.discountType === "fixed") {
      // Fixed discount: exactly one plan (not "All Plans")
      if (
        selectedPlans.length !== 1 ||
        selectedPlans.includes("All Plans") ||
        selectedPlans.includes("all")
      ) {
        return false
      }
    }

    // If banner is enabled, both language fields are required
    if (formData.showBanner) {
      if (
        !formData.bannerTextEnglish?.trim() ||
        !formData.bannerTextArabic?.trim() ||
        countChars(formData.bannerTextEnglish) > MAX_BANNER_CHARS ||
        countChars(formData.bannerTextArabic) > MAX_BANNER_CHARS
      ) {
        return false
      }
    }

    // All validations passed
    return true
  }

  const handleNext = () => {
    if (currentStep === 0) {
      setCurrentStep(1)
    } else {
      // Show confirm modal when clicking Next on audience step
      setShowConfirmModal(true)
    }
  }

  const buildSubmitPayload = (payloadFormData = formData) => ({
    ...payloadFormData,
    show_banner: payloadFormData.showBanner,
    english_text: payloadFormData.bannerTextEnglish,
    arabic_text: payloadFormData.bannerTextArabic,
  })

  const handleConfirmSubmit = () => {
    onSubmit && onSubmit(buildSubmitPayload())
    setShowConfirmModal(false)
    handleClose(true)
  }

  const handleDiscard = () => {
    setShowConfirmModal(false)
    // Stay on the audience step
  }

  const handleBack = async () => {
    if (currentStep === 1) {
      setCurrentStep(0)
    } else {
      await handleClose()
    }
  }

  // Helper function to check if form data has changed from original editData
  const hasFormDataChanged = () => {
    if (!editData || !editData.id) {
      return false
    }

    // Normalize applicablePlans for comparison
    const normalizePlans = (plans) => {
      if (!plans || plans.length === 0) return []
      return Array.isArray(plans) ? plans : [plans]
    }

    // Normalize targetCountries for comparison
    const normalizeCountries = (countries) => {
      if (!countries || countries.length === 0) return []
      return Array.isArray(countries) ? countries : [countries]
    }

    // Compare dates - handle dayjs objects and strings
    const compareDates = (date1, date2) => {
      if (!date1 && !date2) return true
      if (!date1 || !date2) return false
      const d1 = dayjs.isDayjs(date1) ? date1 : dayjs(date1)
      const d2 = dayjs.isDayjs(date2) ? date2 : dayjs(date2)
      return d1.isSame(d2, "day")
    }

    // Compare times - handle dayjs objects and strings
    const compareTimes = (time1, time2) => {
      if (!time1 && !time2) return true
      if (!time1 || !time2) return false
      const t1 =
        typeof time1 === "string"
          ? time1
          : dayjs.isDayjs(time1)
          ? time1.format("HH:mm")
          : ""
      const t2 =
        typeof time2 === "string"
          ? time2
          : dayjs.isDayjs(time2)
          ? time2.format("HH:mm")
          : ""
      return t1 === t2
    }

    // Compare arrays (for selectedUsers) - handle both IDs and objects
    const compareArrays = (arr1, arr2) => {
      if (!arr1 && !arr2) return true
      if (!arr1 || !arr2) return false
      if (arr1.length !== arr2.length) return false

      // Extract IDs from both arrays (handle both object format {id: X} and direct ID)
      const ids1 = arr1
        .map((item) => {
          if (typeof item === "object" && item !== null) {
            return item.id || item
          }
          return item
        })
        .sort()

      const ids2 = arr2
        .map((item) => {
          if (typeof item === "object" && item !== null) {
            return item.id || item
          }
          return item
        })
        .sort()

      return JSON.stringify(ids1) === JSON.stringify(ids2)
    }

    // Check each field for changes
    if (formData.discountNameEnglish !== (editData.discountNameEnglish || ""))
      return true
    if (formData.discountNameArabic !== (editData.discountNameArabic || ""))
      return true
    if (formData.discountCode !== (editData.discountCode || "")) return true
    if (formData.discountType !== (editData.discountType || "percentage"))
      return true
    if (formData.discountType === "percentage") {
      if (
        parseFloat(formData.discountPercentage) !==
        parseFloat(editData.discountPercentage || 0)
      )
        return true
    } else {
      if (
        parseFloat(formData.discountFixed) !==
        parseFloat(editData.discountFixed || 0)
      )
        return true
    }
    if (
      JSON.stringify(normalizePlans(formData.applicablePlans)) !==
      JSON.stringify(normalizePlans(editData.applicablePlans))
    )
      return true
    if (
      JSON.stringify(normalizeCountries(formData.targetCountries)) !==
      JSON.stringify(normalizeCountries(editData.targetCountries))
    )
      return true
    if (!compareDates(formData.startDate, editData.startDate)) return true
    if (!compareTimes(formData.startTime, editData.startTime)) return true
    if (!compareDates(formData.endDate, editData.endDate)) return true
    if (!compareTimes(formData.endTime, editData.endTime)) return true
    const formMaxUses =
      formData.maxUses === "" ? 0 : parseInt(formData.maxUses) || 0
    const editMaxUses =
      editData.maxUses === "" || !editData.maxUses
        ? 0
        : parseInt(editData.maxUses) || 0
    if (formMaxUses !== editMaxUses) return true
    if (
      formData.notifyWhenMaxReached !== (editData.notifyWhenMaxReached || false)
    )
      return true
    if (formData.firstPurchaseOnly !== (editData.firstPurchaseOnly || false))
      return true
    if (formData.redeemMoreThanOnce !== (editData.redeemMoreThanOnce || false))
      return true
    const editShowBanner =
      editData.showBanner ?? editData.show_banner ?? false
    if (formData.showBanner !== editShowBanner) return true
    const editBannerEnglish =
      editData.bannerTextEnglish ??
      editData.english_text ??
      editData.banner_text_english ??
      ""
    const editBannerArabic =
      editData.bannerTextArabic ??
      editData.arabic_text ??
      editData.banner_text_arabic ??
      ""
    if ((formData.bannerTextEnglish || "") !== editBannerEnglish) return true
    if ((formData.bannerTextArabic || "") !== editBannerArabic) return true
    if (formData.audience !== (editData.audience || "")) return true
    if (
      !compareArrays(formData.selectedUsers || [], editData.selectedUsers || [])
    )
      return true

    return false
  }

  const handleClose = async (skipDiscardCheck = false) => {
    // Skip discard modal if explicitly requested (already saved via handleConfirmSubmit)
    if (skipDiscardCheck) {
      // Direct close without showing discard modal or saving again
      setCurrentStep(0)
      setFormData({
        discountNameEnglish: "",
        discountNameArabic: "",
        discountCode: "",
        discountType: "percentage",
        discountPercentage: 0,
        discountFixed: 0,
        applicablePlans: [],
        targetCountries: [],
        startDate: null,
        startTime: null,
        endDate: null,
        endTime: null,
        maxUses: "",
        notifyWhenMaxReached: false,
        firstPurchaseOnly: false,
        redeemMoreThanOnce: false,
        showBanner: false,
        bannerTextEnglish: "",
        bannerTextArabic: "",
        audience: "",
        selectedUsers: [],
        planExtensionMonths: 1,
      })
      setPlanExtensionInput("1")
      setDiscountCodeError("")
      setFixedDiscountError("")
      onClose()
      return
    }

    // If in edit mode, check if any changes were made
    if (editData && editData.id) {
      // Only show discard modal if changes were made
      if (hasFormDataChanged()) {
        setShowDiscardModal(true)
        return
      } else {
        // No changes made, close directly
        setCurrentStep(0)
        setFormData({
          discountNameEnglish: "",
          discountNameArabic: "",
          discountCode: "",
          discountType: "percentage",
          discountPercentage: 0,
          discountFixed: 0,
          applicablePlans: [],
          targetCountries: [],
          startDate: null,
          startTime: null,
          endDate: null,
          endTime: null,
          maxUses: "",
          notifyWhenMaxReached: false,
          firstPurchaseOnly: false,
          redeemMoreThanOnce: false,
          showBanner: false,
          bannerTextEnglish: "",
          bannerTextArabic: "",
          audience: "",
          selectedUsers: [],
          planExtensionMonths: 1,
        })
        setPlanExtensionInput("1")
        setDiscountCodeError("")
        setFixedDiscountError("")
        onClose()
        return
      }
    }

    // For create mode, check if there's any data filled
    const hasData =
      formData.discountNameEnglish ||
      formData.discountNameArabic ||
      formData.discountCode ||
      formData.discountPercentage > 0 ||
      formData.discountFixed > 0 ||
      formData.startDate ||
      formData.startTime ||
      formData.endDate ||
      formData.endTime ||
      (formData.maxUses && formData.maxUses !== "" && formData.maxUses > 0) ||
      formData.showBanner ||
      formData.bannerTextEnglish ||
      formData.bannerTextArabic ||
      formData.audience ||
      (formData.selectedUsers && formData.selectedUsers.length > 0)

    if (hasData) {
      // Show discard modal
      setShowDiscardModal(true)
    } else {
      // No data, just close
      setCurrentStep(0)
      setFormData({
        discountNameEnglish: "",
        discountNameArabic: "",
        discountCode: "",
        discountType: "percentage",
        discountPercentage: 0,
        discountFixed: 0,
        applicablePlans: [],
        targetCountries: [],
        startDate: null,
        startTime: null,
        endDate: null,
        endTime: null,
        maxUses: "",
        notifyWhenMaxReached: false,
        firstPurchaseOnly: false,
        redeemMoreThanOnce: false,
        showBanner: false,
        bannerTextEnglish: "",
        bannerTextArabic: "",
        audience: "",
        selectedUsers: [],
        planExtensionMonths: 1,
      })
      setPlanExtensionInput("1")
      setDiscountCodeError("")
      setFixedDiscountError("")
      onClose()
    }
  }

  const handleDiscardConfirm = async () => {
    // If in edit mode, save changes before closing
    if (editData && editData.id) {
      try {
        // Call onSubmit to save changes
        if (onSubmit) {
          await onSubmit(buildSubmitPayload())
        }
        // Close modal after successful save
        setShowDiscardModal(false)
        setCurrentStep(0)
        setFormData({
          discountNameEnglish: "",
          discountNameArabic: "",
          discountCode: "",
          discountType: "percentage",
          discountPercentage: 0,
          discountFixed: 0,
          applicablePlans: [],
          targetCountries: [],
          startDate: null,
          startTime: null,
          endDate: null,
          endTime: null,
          maxUses: "",
          notifyWhenMaxReached: false,
          firstPurchaseOnly: false,
          redeemMoreThanOnce: false,
          showBanner: false,
          bannerTextEnglish: "",
          bannerTextArabic: "",
          audience: "",
          selectedUsers: [],
          planExtensionMonths: 1,
        })
        setPlanExtensionInput("1")
        setDiscountCodeError("")
        setFixedDiscountError("")
        onClose()
      } catch (error) {
        // If save fails, don't close the modal - let user see the error
        console.error("Error saving changes:", error)
        setShowDiscardModal(false)
        // Modal stays open so user can see the error
      }
    } else {
      // For create mode, just discard
      setShowDiscardModal(false)
      setCurrentStep(0)
      setFormData({
        discountNameEnglish: "",
        discountNameArabic: "",
        discountCode: "",
        discountType: "percentage",
        discountPercentage: 0,
        discountFixed: 0,
        applicablePlans: "All Plans",
        targetCountries: "All Countries",
        startDate: null,
        startTime: null,
        endDate: null,
        endTime: null,
        maxUses: "",
        notifyWhenMaxReached: false,
        firstPurchaseOnly: false,
        redeemMoreThanOnce: false,
        showBanner: false,
        bannerTextEnglish: "",
        bannerTextArabic: "",
        audience: "",
        selectedUsers: [],
        planExtensionMonths: 1,
      })
      setDiscountCodeError("")
      setFixedDiscountError("")
      setPlanExtensionInput("1")
      onClose()
    }
  }

  const handleDiscardWithoutSaving = () => {
    // Discard changes without saving (only for edit mode)
    setShowDiscardModal(false)
    setCurrentStep(0)
    setFormData({
      discountNameEnglish: "",
      discountNameArabic: "",
      discountCode: "",
      discountType: "percentage",
      discountPercentage: 0,
      discountFixed: 0,
      applicablePlans: [],
      targetCountries: [],
      startDate: null,
      startTime: null,
      endDate: null,
      endTime: null,
      maxUses: 0,
      notifyWhenMaxReached: false,
      firstPurchaseOnly: false,
      redeemMoreThanOnce: false,
      showBanner: false,
      bannerTextEnglish: "",
      bannerTextArabic: "",
      audience: "",
      selectedUsers: [],
      planExtensionMonths: 1,
    })
    setDiscountCodeError("")
    setPlanExtensionInput("1")
    onClose()
  }

  const handleDiscardCancel = () => {
    // In edit mode, cancel means discard changes (close without saving)
    if (editData && editData.id) {
      handleDiscardWithoutSaving()
    } else {
      // In create mode, just close the modal
      setShowDiscardModal(false)
    }
  }

  // Fetch plans from API when modal opens
  useEffect(() => {
    const fetchPlans = async () => {
      if (!visible) return

      try {
        setPlansLoading(true)
        const response = await DiscountsApi.getPlans()
        const rawPlans = extractPlansArrayFromGetPlansResponse(response?.data)

        if (rawPlans.length > 0) {
          setPlans([{ id: "all", name: "All Plans" }, ...rawPlans])
        }
      } catch (error) {
        console.error("Error fetching plans:", error)
        message.error(
          t?.error_fetching_plans ||
            "Error fetching plans. Using default options."
        )
        // Set default plans on error
        setPlans([
          { id: "all", name: "All Plans" },
          { id: 1, name: "Basic" },
          { id: 4, name: "Pro" },
          { id: 6, name: "Starter" },
          { id: 7, name: "Business" },
        ])
      } finally {
        setPlansLoading(false)
      }
    }

    fetchPlans()
  }, [visible, t])

  // Fetch countries list from API
  useEffect(() => {
    if (!visible) return

    const fetchCountries = async () => {
      try {
        setCountriesLoading(true)
        const response = await DiscountsApi.getCountryFilter()
        const countryCodes = response?.data?.result?.available_countries || []

        const formatter = new Intl.DisplayNames([lang === "ar" ? "ar" : "en"], {
          type: "region",
        })

        const formattedCountries = countryCodes
          .map((code) => {
            const name = formatter.of(code) || code
            return { code, name }
          })
          .sort((a, b) =>
            a.name.localeCompare(b.name, lang === "ar" ? "ar" : "en")
          )

        setCountries([
          { code: "all", name: "All Countries" },
          ...formattedCountries,
        ])
      } catch (error) {
        console.error("Error fetching countries:", error)
        setCountries([{ code: "all", name: "All Countries" }])
      } finally {
        setCountriesLoading(false)
      }
    }

    fetchCountries()
  }, [visible, lang])

  const isBannerEnglishMissing =
    formData.showBanner && !formData.bannerTextEnglish?.trim()
  const isBannerArabicMissing =
    formData.showBanner && !formData.bannerTextArabic?.trim()
  const bannerEnglishCharCount = countChars(formData.bannerTextEnglish)
  const bannerArabicCharCount = countChars(formData.bannerTextArabic)
  const isBannerEnglishTooLong =
    formData.showBanner && bannerEnglishCharCount > MAX_BANNER_CHARS
  const isBannerArabicTooLong =
    formData.showBanner && bannerArabicCharCount > MAX_BANNER_CHARS

  const steps = [
    {
      key: "basic",
      title: t?.modal?.basic_info || "Basic Info",
      icon: <InfoCircleOutlined className="create-discount-step-icon" />,
    },
    {
      key: "audience",
      title: t?.modal?.audience || "Audience",
      icon: <UsergroupAddOutlined className="create-discount-step-icon" />,
    },
  ]

  return (
    <>
      <Modal
        title={
          <div className="create-discount-modal-title-text">
            {editData
              ? t?.modal?.edit_discount_code || "Edit Discount Code"
              : t?.modal?.create_discount_code || "Create Discount Code"}
          </div>
        }
        open={visible && !showConfirmModal && !showDiscardModal}
        onCancel={() => handleClose(false)}
        footer={null}
        width={900}
        centered
        className="create-discount-modal"
        closeIcon={
          <span
            className="create-discount-modal-close-icon"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleClose(false)
            }}
            style={{ cursor: "pointer" }}
          >
            ×
          </span>
        }
      >
        <div className="create-discount-modal-content-wrapper">
          {/* Progress Tracker */}
          <div
            className="discount-progress-tracker"
            style={{ marginBottom: "32px" }}
          >
            {steps.map((step, index) => {
              const isCompleted = index < currentStep
              const isCurrent = index === currentStep
              const isFuture = index > currentStep

              // Get icon based on step state
              let stepIcon = step.icon
              if (index === 0) {
                // Basic Info step - use info icon
                stepIcon = (
                  <InfoCircleOutlined className="create-discount-step-icon" />
                )
              } else if (index === 1) {
                // Audience step - use user group icons
                stepIcon = (
                  <img
                    src={
                      isCompleted
                        ? "/Campaign/user-group-white.svg"
                        : "/Campaign/user-group-cam.svg"
                    }
                    alt="Audience"
                    className="create-discount-step-image"
                  />
                )
              }

              return (
                <React.Fragment key={step.key}>
                  <div className="discount-progress-step-wrapper">
                    <div
                      className={`discount-progress-step-icon ${
                        isCompleted
                          ? "discount-progress-step-completed"
                          : isCurrent
                          ? "discount-progress-step-current"
                          : "discount-progress-step-future"
                      }`}
                    >
                      <div className="discount-progress-step-icon-content">
                        {stepIcon}
                      </div>
                    </div>
                    <div
                      className={`discount-progress-step-title ${
                        isCompleted
                          ? "discount-progress-step-title-completed"
                          : isCurrent
                          ? "discount-progress-step-title-current"
                          : "discount-progress-step-title-future"
                      }`}
                    >
                      {step.title}
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`discount-progress-step-connector ${
                        isCompleted
                          ? "discount-progress-step-connector-completed"
                          : "discount-progress-step-connector-future"
                      }`}
                    />
                  )}
                </React.Fragment>
              )
            })}
          </div>

          {/* Step Content */}
          <div className="create-discount-modal-step-content">
            {currentStep === 0 && (
              <div>
                {/* Discount Name English and Arabic in one row */}
                <div
                  style={{ display: "flex", gap: "16px", marginBottom: "16px" }}
                >
                  <div style={{ flex: 1 }}>
                    <DiscountFieldLabel required>
                      {t?.modal?.discount_name_english ||
                        "Discount Name (English)"}
                    </DiscountFieldLabel>
                    <Input
                      value={formData.discountNameEnglish}
                      onChange={(e) =>
                        handleInputChange("discountNameEnglish", e.target.value)
                      }
                      placeholder={
                        t?.modal?.discount_name_english ||
                        "Discount Name (English)"
                      }
                      style={{
                        height: "46px",
                        borderRadius: "4px",
                        border: "1px solid #E5E7EB",
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <DiscountFieldLabel>
                      {t?.modal?.discount_name_arabic ||
                        "Discount Name (Arabic)"}
                    </DiscountFieldLabel>
                    <Input
                      value={formData.discountNameArabic}
                      onChange={(e) =>
                        handleInputChange("discountNameArabic", e.target.value)
                      }
                      placeholder={
                        t?.modal?.discount_name_arabic ||
                        "Discount Name (Arabic)"
                      }
                      style={{
                        height: "46px",
                        borderRadius: "4px",
                        border: "1px solid #E5E7EB",
                        textAlign: "right",
                      }}
                    />
                  </div>
                </div>

                {/* Discount Code and Discount Type in one row */}
                <div
                  style={{ display: "flex", gap: "16px", marginBottom: "16px" }}
                >
                  <div style={{ flex: 1 }}>
                    <DiscountFieldLabel required>
                      {t?.modal?.discount_code || "Discount Code"}
                    </DiscountFieldLabel>
                    <Input
                      value={formData.discountCode}
                      onChange={(e) =>
                        handleInputChange("discountCode", e.target.value)
                      }
                      placeholder={t?.modal?.discount_code || "Discount Code"}
                      maxLength={20}
                      style={{
                        height: "46px",
                        borderRadius: "4px",
                        border: discountCodeError
                          ? "1px solid #F04438"
                          : "1px solid #E5E7EB",
                      }}
                    />
                    {discountCodeError && (
                      <div
                        style={{
                          color: "#F04438",
                          fontSize: "12px",
                          marginTop: "4px",
                        }}
                      >
                        {discountCodeError}
                      </div>
                    )}
                    {!discountCodeError && formData.discountCode && (
                      <div
                        style={{
                          color: "#667085",
                          fontSize: "12px",
                          marginTop: "4px",
                        }}
                      >
                        {formData.discountCode.length < 3
                          ? `${
                              3 - formData.discountCode.length
                            } more characters required`
                          : `${formData.discountCode.length}/20 characters`}
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <DiscountFieldLabel required>
                      {t?.modal?.discount_type || "Discount Type"}
                    </DiscountFieldLabel>
                    <Select
                      value={formData.discountType}
                      onChange={(value) => {
                        handleInputChange("discountType", value)
                        // If switching to fixed, clear "All Plans" or multiple selections
                        if (value === "fixed") {
                          const currentPlans = formData.applicablePlans || []
                          if (
                            Array.isArray(currentPlans) &&
                            (currentPlans.includes("All Plans") ||
                              currentPlans.includes("all") ||
                              currentPlans.length > 1)
                          ) {
                            // Clear selections when switching to fixed
                            handleInputChange("applicablePlans", [])
                          }
                          // Clear fixed discount error when switching types
                          setFixedDiscountError("")
                        } else {
                          // Clear fixed discount error when switching to percentage
                          setFixedDiscountError("")
                        }
                      }}
                      style={{
                        width: "100%",
                        height: "46px",
                      }}
                      className="discount-filter-select"
                    >
                      <Option value="percentage">
                        {t?.modal?.percentage_discount || "Percentage Discount"}
                      </Option>
                      <Option value="fixed">
                        {t?.modal?.fixed_discount || "Fixed Discount"}
                      </Option>
                      <Option value="plan_extension">
                        {t?.modal?.plan_extension || "Plan Extension"}
                      </Option>
                    </Select>
                  </div>
                </div>

                {/* Discount Percentage/Fixed - Full Row */}
                {formData.discountType === "percentage" && (
                  <div style={{ marginBottom: "16px" }}>
                    <DiscountFieldLabel required>
                      {t?.modal?.discount_percentage || "Discount Percentage"}
                    </DiscountFieldLabel>
                    <div style={{ position: "relative" }}>
                      <Input
                        type="number"
                        min="0"
                        max="99"
                        value={formData.discountPercentage}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 0
                          const clampedValue =
                            value < 0 ? 0 : value > 99 ? 99 : value
                          handleInputChange("discountPercentage", clampedValue)
                        }}
                        onWheel={(e) => e.target.blur()}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                            e.preventDefault()
                          }
                        }}
                        style={{
                          height: "46px",
                          borderRadius: "4px",
                          paddingRight: "32px",
                        }}
                      />
                      <span
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#667085",
                          fontSize: "14px",
                          pointerEvents: "none",
                        }}
                      >
                        %
                      </span>
                    </div>
                  </div>
                )}

                {formData.discountType === "fixed" && (
                  <div style={{ marginBottom: "16px" }}>
                    <DiscountFieldLabel required>
                      {t?.modal?.discount_amount || "Discount Amount"}
                    </DiscountFieldLabel>
                    <Input
                      type="number"
                      min="0"
                      max="99999"
                      value={formData.discountFixed}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0
                        const clampedValue =
                          value < 0 ? 0 : value > 99999 ? 99999 : value

                        // Validate against maximum limit
                        let error = ""
                        if (value > 99999) {
                          error =
                            t?.modal?.error_discount_max_amount ||
                            "Discount amount cannot exceed 99,999 SAR"
                        } else if (
                          formData.applicablePlans &&
                          formData.applicablePlans.length > 0
                        ) {
                          const selectedPlanName = formData.applicablePlans[0]
                          const minPrice =
                            getMinPriceForPlanName(selectedPlanName)

                          if (minPrice !== null && minPrice > 0) {
                            if (clampedValue >= minPrice) {
                              error = `${
                                t?.modal?.error_discount_exceeds_plan_price ||
                                "Discount amount must be less than plan price (SAR"
                              } ${minPrice})`
                            }
                          }
                        }

                        setFixedDiscountError(error)
                        handleInputChange("discountFixed", clampedValue)
                      }}
                      onWheel={(e) => e.target.blur()}
                      onKeyDown={(e) => {
                        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                          e.preventDefault()
                        }
                      }}
                      addonBefore="SAR"
                      style={{
                        height: "46px",
                        borderRadius: "4px",
                        border: fixedDiscountError
                          ? "1px solid #F04438"
                          : "1px solid #E5E7EB",
                      }}
                    />
                    {fixedDiscountError && (
                      <div
                        style={{
                          color: "#F04438",
                          fontSize: "12px",
                          marginTop: "4px",
                        }}
                      >
                        {fixedDiscountError}
                      </div>
                    )}
                  </div>
                )}

                {formData.discountType === "plan_extension" && (
                  <div style={{ marginBottom: "16px" }}>
                    <DiscountFieldLabel required>
                      {t?.modal?.number_of_months || "Number of Months"}
                    </DiscountFieldLabel>
                    <div style={{ position: "relative" }}>
                      <Input
                        type="number"
                        min="1"
                        max="10"
                        value={planExtensionInput}
                        onChange={(e) => {
                          const raw = e.target.value
                          // Allow temporary empty input while typing
                          if (raw === "") {
                            setPlanExtensionInput("")
                            return
                          }

                          // Keep only digits
                          const digitsOnly = raw.replace(/\D/g, "")
                          if (digitsOnly === "") {
                            setPlanExtensionInput("")
                            return
                          }

                          const parsed = parseInt(digitsOnly, 10)
                          if (Number.isNaN(parsed)) {
                            setPlanExtensionInput("")
                            return
                          }

                          const clamped = Math.max(1, Math.min(10, parsed))
                          setPlanExtensionInput(String(clamped))
                          handleInputChange("planExtensionMonths", clamped)
                        }}
                        onBlur={(e) => {
                          const parsed = parseInt(e.target.value, 10)
                          const finalValue = Number.isNaN(parsed)
                            ? 1
                            : Math.max(1, Math.min(10, parsed))
                          setPlanExtensionInput(String(finalValue))
                          handleInputChange("planExtensionMonths", finalValue)
                        }}
                        onWheel={(e) => e.target.blur()}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                            e.preventDefault()
                          }
                        }}
                        style={{
                          height: "46px",
                          borderRadius: "4px",
                          paddingRight: "40px",
                        }}
                      />
                      <span
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#667085",
                          fontSize: "14px",
                          pointerEvents: "none",
                        }}
                      >
                        months
                      </span>
                    </div>
                  </div>
                )}

                {/* Applicable Plans and Target Countries in one row */}
                <div
                  style={{ display: "flex", gap: "16px", marginBottom: "16px" }}
                >
                  <div style={{ flex: 1 }}>
                    <DiscountFieldLabel required>
                      {t?.modal?.applicable_plans || "Applicable Plans"}
                    </DiscountFieldLabel>
                    <Select
                      mode={
                        formData.discountType === "fixed"
                          ? undefined
                          : "multiple"
                      }
                      value={
                        formData.discountType === "fixed"
                          ? Array.isArray(formData.applicablePlans) &&
                            formData.applicablePlans.length > 0
                            ? formData.applicablePlans[0]
                            : undefined
                          : formData.applicablePlans || []
                      }
                      onChange={(value) => {
                        if (formData.discountType === "fixed") {
                          // For fixed discount, only allow single plan selection
                          // Prevent "All Plans" selection
                          if (value === "All Plans" || value === "all") {
                            // Don't allow "All Plans" for fixed discounts
                            return
                          }
                          // Store as single value (not array)
                          handleInputChange(
                            "applicablePlans",
                            value ? [value] : []
                          )

                          // Re-validate fixed discount value against new plan price and maximum limit
                          if (value && formData.discountFixed > 0) {
                            let error = ""

                            // Check maximum limit first
                            if (formData.discountFixed > 99999) {
                              error =
                                t?.modal?.error_discount_max_amount ||
                                "Discount amount cannot exceed 99,999 SAR"
                            } else {
                              const minPrice = getMinPriceForPlanName(value)
                              if (minPrice !== null && minPrice > 0) {
                                if (formData.discountFixed >= minPrice) {
                                  error = `${
                                    t?.modal
                                      ?.error_discount_exceeds_plan_price ||
                                    "Discount amount must be less than plan price (SAR"
                                  } ${minPrice})`
                                }
                              }
                            }

                            setFixedDiscountError(error)
                          } else {
                            // Plan cleared, clear error
                            setFixedDiscountError("")
                          }
                        } else {
                          // For percentage discount, allow multiple plans
                          // Ensure value is always an array
                          let newValue = Array.isArray(value) ? value : []

                          // Check if "All Plans" is in the selection
                          const hasAllPlans =
                            newValue.includes("All Plans") ||
                            newValue.includes("all")

                          if (hasAllPlans) {
                            // If "All Plans" is selected, only keep "All Plans" (or "all")
                            newValue = newValue.filter(
                              (item) => item === "All Plans" || item === "all"
                            )
                            // Ensure we have "All Plans" in the array
                            if (
                              !newValue.includes("All Plans") &&
                              !newValue.includes("all")
                            ) {
                              newValue = ["All Plans"]
                            }
                          } else {
                            // If "All Plans" is not selected, remove it if it exists
                            newValue = newValue.filter(
                              (item) => item !== "All Plans" && item !== "all"
                            )
                          }

                          handleInputChange("applicablePlans", newValue)
                        }
                      }}
                      loading={plansLoading}
                      style={{
                        width: "100%",
                      }}
                      className="discount-filter-select applicable-plans-select"
                      placeholder={
                        formData.discountType === "fixed"
                          ? t?.modal?.select_plan || "Select Plan"
                          : t?.modal?.all_plans || "All Plans"
                      }
                      maxTagCount="responsive"
                      allowClear
                      notFoundContent={
                        plansLoading ? "Loading..." : "No plans found"
                      }
                      options={applicablePlanSelectOptions
                        .filter((plan) => {
                          if (formData.discountType === "fixed") {
                            return plan.name !== "All Plans"
                          }
                          return true
                        })
                        .map((plan) => ({
                          label: plan.name,
                          value: plan.name,
                        }))}
                      tagRender={
                        formData.discountType === "fixed"
                          ? undefined
                          : (props) => {
                              const { label, value, closable, onClose } = props
                              return (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    background: "#F9FAFB",
                                    border: "1px solid #E4E7EC",
                                    borderRadius: "6px",
                                    padding: "2px 8px",
                                    margin: "2px 4px 2px 0",
                                    fontSize: "12px",
                                    color: "#344054",
                                    height: "24px",
                                    lineHeight: "20px",
                                  }}
                                >
                                  {label}
                                  {closable && (
                                    <span
                                      onClick={onClose}
                                      style={{
                                        marginLeft: "4px",
                                        cursor: "pointer",
                                        color: "#667085",
                                      }}
                                    >
                                      ×
                                    </span>
                                  )}
                                </span>
                              )
                            }
                      }
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <DiscountFieldLabel>
                      {t?.modal?.target_countries || "Target Countries"}
                    </DiscountFieldLabel>
                    <Select
                      mode="multiple"
                      value={formData.targetCountries || []}
                      onChange={async (value) => {
                        // Filter out "All Countries" if it's selected with other countries
                        const filteredValue = value.filter(
                          (v) => v !== "All Countries" && v !== "all"
                        )

                        // Only call API if countries are actually selected and different from current
                        const currentCountries = formData.targetCountries || []
                        const hasChanged =
                          filteredValue.length !== currentCountries.length ||
                          filteredValue.some(
                            (c, i) => c !== currentCountries[i]
                          )

                        handleInputChange("targetCountries", filteredValue)

                        // Call API only when countries are selected and value has changed
                        if (filteredValue.length > 0 && hasChanged) {
                          try {
                            const response = await DiscountsApi.getUsersCount(
                              filteredValue
                            )
                            // You can handle the response here if needed
                            console.log("Users count:", response?.data)
                          } catch (error) {
                            console.error("Error fetching users count:", error)
                            message.error(
                              t?.error_fetching_users_count ||
                                "Error fetching users count"
                            )
                          }
                        }
                      }}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      style={{
                        width: "100%",
                      }}
                      className="discount-filter-select"
                      placeholder={t?.modal?.select_country || "Select Country"}
                      dropdownStyle={{
                        cursor: "default",
                      }}
                      popupClassName="target-countries-dropdown"
                      maxTagCount="responsive"
                      allowClear
                      loading={countriesLoading}
                      notFoundContent={
                        countriesLoading ? "Loading..." : "No countries found"
                      }
                    >
                      {countries
                        .filter((country) => country.code !== "all")
                        .map((country) => (
                          <Option
                            key={country.code}
                            value={country.code}
                            label={country.name}
                          >
                            <img
                              src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`}
                              alt={country.code}
                              style={{
                                width: "20px",
                                height: "15px",
                                marginRight: "8px",
                                verticalAlign: "middle",
                              }}
                              onError={(e) => {
                                e.target.style.display = "none"
                              }}
                            />
                            {country.name}
                          </Option>
                        ))}
                    </Select>
                  </div>
                </div>

                {/* Start Date/Time and End Date/Time in one row */}
                <div
                  style={{ display: "flex", gap: "16px", marginBottom: "16px" }}
                >
                  <div style={{ flex: 1 }}>
                    <DiscountFieldLabel required>
                      {t?.modal?.start_date_time || "Start Date/Time"}
                    </DiscountFieldLabel>
                    <div style={{ display: "flex", gap: "12px" }}>
                      <DatePicker
                        popupClassName="discount-date-picker-popup"
                        value={
                          formData.startDate ? dayjs(formData.startDate) : null
                        }
                        onChange={(date) =>
                          handleInputChange(
                            "startDate",
                            date ? date.format("YYYY-MM-DD") : null
                          )
                        }
                        format="DD/MM/YYYY"
                        placeholder="DD/MM/YYYY"
                        disabledDate={(current) =>
                          current && current < dayjs().startOf("day")
                        }
                        disabled={
                          editData &&
                          editData.id &&
                          editData.status &&
                          editData.status.toLowerCase() === "active"
                        }
                        style={{
                          flex: 1,
                          height: "46px",
                          borderRadius: "4px",
                        }}
                      />
                      <TimePicker
                        popupClassName="discount-time-picker-popup"
                        value={
                          formData.startTime
                            ? dayjs(formData.startTime, "HH:mm")
                            : null
                        }
                        onChange={(time) =>
                          handleInputChange(
                            "startTime",
                            time ? time.format("HH:mm") : null
                          )
                        }
                        format="HH:mm"
                        placeholder="-- : -- --"
                        disabled={
                          editData &&
                          editData.id &&
                          editData.status &&
                          editData.status.toLowerCase() === "active"
                        }
                        style={{
                          flex: 1,
                          height: "46px",
                          borderRadius: "4px",
                        }}
                      />
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <DiscountFieldLabel required>
                      {t?.modal?.end_date_time || "End Date/Time"}
                    </DiscountFieldLabel>
                    <div style={{ display: "flex", gap: "12px" }}>
                      <DatePicker
                        popupClassName="discount-date-picker-popup"
                        value={
                          formData.endDate ? dayjs(formData.endDate) : null
                        }
                        onChange={(date) =>
                          handleInputChange(
                            "endDate",
                            date ? date.format("YYYY-MM-DD") : null
                          )
                        }
                        format="DD/MM/YYYY"
                        placeholder="DD/MM/YYYY"
                        disabledDate={(current) => {
                          const today = dayjs().startOf("day")
                          const startDate = formData.startDate
                            ? dayjs(formData.startDate).startOf("day")
                            : null
                          // Disable dates before today or before start date (if start date is selected)
                          if (startDate) {
                            return (
                              current &&
                              (current < today || current < startDate)
                            )
                          }
                          return current && current < today
                        }}
                        style={{
                          flex: 1,
                          height: "46px",
                          borderRadius: "4px",
                        }}
                      />
                      <TimePicker
                        popupClassName="discount-time-picker-popup"
                        value={
                          formData.endTime
                            ? dayjs(formData.endTime, "HH:mm")
                            : null
                        }
                        onChange={(time) =>
                          handleInputChange(
                            "endTime",
                            time ? time.format("HH:mm") : null
                          )
                        }
                        format="HH:mm"
                        placeholder="-- : -- --"
                        style={{
                          flex: 1,
                          height: "46px",
                          borderRadius: "4px",
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Max Uses Overall and Notify When Max Uses Reached in one row */}
                <div
                  style={{ display: "flex", gap: "16px", marginBottom: "16px" }}
                >
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#141414",
                      }}
                    >
                      {t?.modal?.max_uses_overall || "Max Uses Overall"}
                    </label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.maxUses === "" ? "" : formData.maxUses}
                      onChange={(e) => {
                        const inputValue = e.target.value
                        // Allow empty string
                        if (inputValue === "") {
                          handleInputChange("maxUses", "")
                          handleInputChange("notifyWhenMaxReached", false)
                          return
                        }
                        const value = parseInt(inputValue)
                        if (isNaN(value)) {
                          return
                        }
                        const nonNegativeValue = value < 0 ? 0 : value
                        handleInputChange("maxUses", nonNegativeValue)
                        // If maxUses is empty or 0, disable and uncheck the notification toggle
                        if (!nonNegativeValue || nonNegativeValue === 0) {
                          handleInputChange("notifyWhenMaxReached", false)
                        }
                      }}
                      onWheel={(e) => e.target.blur()}
                      onKeyDown={(e) => {
                        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                          e.preventDefault()
                        }
                      }}
                      style={{
                        height: "46px",
                        borderRadius: "4px",
                        border: "1px solid #E5E7EB",
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#141414",
                      }}
                    >
                      {t?.modal?.notify_when_max_reached ||
                        "Notify When Max Uses Reached"}
                    </label>
                    <div
                      style={{
                        position: "relative",
                        height: "46px",
                        borderRadius: "4px",
                        border: "1px solid #E5E7EB",
                        backgroundColor: "#F9FAFB",
                        display: "flex",
                        alignItems: "center",
                        padding: "0 12px",
                      }}
                    >
                      <Input
                        disabled
                        value={
                          t?.modal?.enable_disable_notification ||
                          "Enable/Disable email notification"
                        }
                        style={{
                          border: "none",
                          backgroundColor: "transparent",
                          padding: 0,
                          height: "auto",
                          color: "#667085",
                          fontSize: "14px",
                          flex: 1,
                        }}
                      />
                      <Switch
                        checked={formData.notifyWhenMaxReached}
                        onChange={(checked) =>
                          handleInputChange("notifyWhenMaxReached", checked)
                        }
                        disabled={
                          !formData.maxUses ||
                          formData.maxUses === "" ||
                          formData.maxUses === 0
                        }
                        className="discount-notify-switch"
                        style={{ marginLeft: "12px" }}
                      />
                    </div>
                  </div>
                </div>

                {/* First Purchase Only and Redeem more than once in one row */}
                <div
                  style={{ display: "flex", gap: "16px", marginBottom: "16px" }}
                >
                  <div style={{ flex: 1 }}>
                    <Checkbox
                      checked={formData.firstPurchaseOnly}
                      onChange={(e) => {
                        const checked = e.target.checked
                        // If this is checked, uncheck the other one
                        handleInputChange("firstPurchaseOnly", checked)
                        if (checked) {
                          handleInputChange("redeemMoreThanOnce", false)
                        }
                      }}
                    >
                      <span style={{ fontSize: "14px", color: "#344054" }}>
                        {t?.modal?.first_purchase_only || "First Purchase Only"}
                      </span>
                    </Checkbox>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Checkbox
                      checked={formData.redeemMoreThanOnce}
                      onChange={(e) => {
                        const checked = e.target.checked
                        // If this is checked, uncheck the other one
                        handleInputChange("redeemMoreThanOnce", checked)
                        if (checked) {
                          handleInputChange("firstPurchaseOnly", false)
                        }
                      }}
                    >
                      <span style={{ fontSize: "14px", color: "#344054" }}>
                        {t?.modal?.redeem_more_than_once ||
                          "Redeem more than once"}
                      </span>
                    </Checkbox>
                  </div>
                </div>

                {/* Show Banner toggle */}
                <div style={{ marginBottom: formData.showBanner ? "12px" : "16px" }}>
                  <Checkbox
                    checked={formData.showBanner}
                    onChange={(e) => {
                      const checked = e.target.checked
                      handleInputChange("showBanner", checked)
                      if (!checked) {
                        handleFormDataChange({
                          bannerTextEnglish: "",
                          bannerTextArabic: "",
                        })
                      }
                    }}
                  >
                    <span style={{ fontSize: "14px", color: "#344054" }}>
                      {lang === "ar"
                        ? "إظهار البانر"
                        : t?.modal?.show_banner || "Show Banner"}
                    </span>
                  </Checkbox>
                </div>

                {/* Banner texts */}
                {formData.showBanner && (
                  <div
                    style={{
                      display: "flex",
                      gap: "16px",
                      marginBottom: "16px",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <DiscountFieldLabel required>
                        {lang === "ar"
                          ? "نص البانر (الإنجليزية)"
                          : t?.modal?.banner_text_english || "Banner Text (English)"}
                      </DiscountFieldLabel>
                      <Input
                        value={formData.bannerTextEnglish}
                        onChange={(e) =>
                          handleInputChange("bannerTextEnglish", e.target.value)
                        }
                        placeholder={
                          lang === "ar"
                            ? "نص البانر (الإنجليزية)"
                            : t?.modal?.banner_text_english || "Banner Text (English)"
                        }
                        maxLength={MAX_BANNER_CHARS}
                        style={{
                          height: "46px",
                          borderRadius: "4px",
                          border:
                            isBannerEnglishMissing || isBannerEnglishTooLong
                            ? "1px solid #F04438"
                            : "1px solid #E5E7EB",
                        }}
                      />
                      {(isBannerEnglishMissing || isBannerEnglishTooLong) && (
                        <div
                          style={{
                            color: "#F04438",
                            fontSize: "12px",
                            marginTop: "4px",
                          }}
                        >
                          {isBannerEnglishMissing
                            ? lang === "ar"
                              ? "نص البانر مطلوب عند إظهار البانر"
                              : t?.modal?.banner_text_required ||
                                "Banner text is required when showing the banner"
                            : lang === "ar"
                            ? `لا يمكن أن يتجاوز نص البانر ${MAX_BANNER_CHARS} حرف`
                            : t?.modal?.banner_text_max_chars ||
                              `Banner text cannot exceed ${MAX_BANNER_CHARS} characters`}
                        </div>
                      )}
                      <div
                        style={{
                          color: "#667085",
                          fontSize: "12px",
                          marginTop: "4px",
                        }}
                      >
                        {lang === "ar"
                          ? `${bannerEnglishCharCount}/${MAX_BANNER_CHARS} حرف`
                          : `${bannerEnglishCharCount}/${MAX_BANNER_CHARS} characters`}
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <DiscountFieldLabel required>
                        {lang === "ar"
                          ? "نص البانر (العربية)"
                          : t?.modal?.banner_text_arabic || "Banner Text (Arabic)"}
                      </DiscountFieldLabel>
                      <Input
                        value={formData.bannerTextArabic}
                        onChange={(e) =>
                          handleInputChange("bannerTextArabic", e.target.value)
                        }
                        placeholder={
                          lang === "ar"
                            ? "نص البانر (العربية)"
                            : t?.modal?.banner_text_arabic || "Banner Text (Arabic)"
                        }
                        maxLength={MAX_BANNER_CHARS}
                        style={{
                          height: "46px",
                          borderRadius: "4px",
                          border:
                            isBannerArabicMissing || isBannerArabicTooLong
                            ? "1px solid #F04438"
                            : "1px solid #E5E7EB",
                          textAlign: "right",
                        }}
                      />
                      {(isBannerArabicMissing || isBannerArabicTooLong) && (
                        <div
                          style={{
                            color: "#F04438",
                            fontSize: "12px",
                            marginTop: "4px",
                            textAlign: "right",
                          }}
                        >
                          {isBannerArabicMissing
                            ? lang === "ar"
                              ? "نص البانر مطلوب عند إظهار البانر"
                              : t?.modal?.banner_text_required ||
                                "Banner text is required when showing the banner"
                            : lang === "ar"
                            ? `لا يمكن أن يتجاوز نص البانر ${MAX_BANNER_CHARS} حرف`
                            : t?.modal?.banner_text_max_chars ||
                              `Banner text cannot exceed ${MAX_BANNER_CHARS} characters`}
                        </div>
                      )}
                      <div
                        style={{
                          color: "#667085",
                          fontSize: "12px",
                          marginTop: "4px",
                          textAlign: "right",
                        }}
                      >
                        {lang === "ar"
                          ? `${bannerArabicCharCount}/${MAX_BANNER_CHARS} حرف`
                          : `${bannerArabicCharCount}/${MAX_BANNER_CHARS} characters`}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {currentStep === 1 && (
              <DiscountAudienceStep
                formData={formData}
                setFormData={handleFormDataChange}
                viewMode={false}
                discountId={editData?.id || null}
              />
            )}
          </div>

          {/* Footer Buttons */}
          <div className="create-discount-modal-buttons-container">
            <Button
              className="create-discount-modal-button"
              onClick={handleBack}
            >
              {t?.modal?.back || "Back"}
            </Button>
            <Button
              type="primary"
              className="create-discount-modal-button-primary"
              onClick={handleNext}
              disabled={currentStep === 0 && !isStep0Valid()}
            >
              {currentStep === 0
                ? t?.modal?.next || "Next"
                : editData
                ? t?.modal?.update || "Update"
                : t?.modal?.create || "Create"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Confirm Saving Discount Modal */}
      <Modal
        open={showConfirmModal}
        onCancel={handleDiscard}
        footer={null}
        width={500}
        centered
        className="confirm-discount-modal"
        closeIcon={
          <CloseOutlined className="confirm-discount-modal-close-icon" />
        }
      >
        <div className="confirm-discount-content">
          <div className="confirm-discount-icon">
            <div className="confirm-discount-icon-container">
              <img
                src="/discounts/discount-orange.svg"
                alt="Discount"
                style={{ width: "32px", height: "32px" }}
              />
            </div>
          </div>
          <h2 className="confirm-discount-title">
            {t?.modal?.confirm_saving_discount || "Confirm Saving Discount"}
          </h2>
          <p className="confirm-discount-message">
            {t?.modal?.confirm_discount_message ||
              "The discount will saved and be available for the users from the start date mentioned."}
          </p>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              gap: "12px",
              marginTop: "32px",
            }}
          >
            <Button
              onClick={handleDiscard}
              className="create-discount-modal-button"
              style={{ minWidth: "120px" }}
            >
              {t?.modal?.discard || "Discard"}
            </Button>
            <Button
              type="primary"
              onClick={handleConfirmSubmit}
              className="create-discount-modal-button-primary"
              style={{ minWidth: "120px" }}
            >
              {t?.modal?.confirm || "Confirm"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Discard Discount Modal */}
      <Modal
        open={showDiscardModal}
        onCancel={() => setShowDiscardModal(false)}
        footer={null}
        width={500}
        centered
        className="confirm-discount-modal"
        closeIcon={
          <CloseOutlined
            className="confirm-discount-modal-close-icon"
            onClick={() => setShowDiscardModal(false)}
          />
        }
      >
        <div className="confirm-discount-content">
          <div className="confirm-discount-icon">
            <div
              style={{
                width: "64px",
                height: "64px",
                borderRadius: "50%",
                backgroundColor: "#FFF4ED",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                // border: "2px solid #FF6B2C",
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "46px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                <img
                  src="/discounts/discount-orange.svg"
                  alt="Discount"
                  style={{
                    width: "32px",
                    height: "32px",
                    position: "absolute",
                  }}
                />
                <div />
              </div>
            </div>
          </div>
          <h2 className="confirm-discount-title">
            {editData && editData.id
              ? t?.modal?.save_changes || "Save Changes?"
              : t?.modal?.discard_discount || "Discard Discount?"}
          </h2>
          <p className="confirm-discount-message">
            {editData && editData.id
              ? t?.modal?.save_changes_message ||
                "Are you sure you want to save these changes?"
              : t?.modal?.discard_discount_message ||
                "By discarding, you will lose all changes."}
          </p>
          <div
            style={{
              width: "100%",
              display: "flex",
              gap: "12px",
              justifyContent: "space-between",
              marginTop: "32px",
            }}
          >
            <Button
              onClick={handleDiscardCancel}
              className="create-discount-modal-button"
              style={{
                minWidth: "120px",
                height: "46px",
                borderRadius: "50px",
                border: "1px solid #D0D5DD",
                backgroundColor: "#FFFFFF",
                color: "#344054",
              }}
            >
              {editData && editData.id
                ? t?.modal?.discard || "Discard"
                : t?.modal?.cancel || "Cancel"}
            </Button>
            <Button
              onClick={handleDiscardConfirm}
              type="primary"
              style={{
                minWidth: "120px",
                height: "46px",
                borderRadius: "50px",
                backgroundColor: "#3E3A36",
                borderColor: "#3E3A36",
                color: "#FFFFFF",
              }}
            >
              {editData && editData.id
                ? t?.modal?.save || "Save"
                : t?.modal?.discard || "Discard"}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default CreateDiscountModal
